import { useEffect, useState } from 'react'

import { createModal } from '../ModalFactory'
import { AuthContext, useAuth } from 'context/AuthContext'
import { ModalWrapper } from '../ModalWrapper'
import {
    TabStrip,
    TabStripSelectEventArguments,
    TabStripTab,
} from '@progress/kendo-react-layout'
import GeneralForm from './GeneralForm'
import {
    deleteApiRequest,
    postApiRequest,
    putApiRequest,
} from 'utils/fetch-helper'
import { useAppContext } from 'context/AppContext'
import ServiceModalStaff from './Staff'
import {
    ServiceApiItem,
    ServiceApiType,
    ServiceApiTypeEnum,
    ServiceDiscountType,
    ServiceItem,
} from 'types/service'
import Translation from 'components/Translation'
import ServiceModalMemberships from './Memberships'
import {
    GroupClassApi,
    GroupClassAvailabilityApi,
    GroupClassFormItem,
    ModalDataAttributes,
    RecurrenceSchedule,
    UserModel,
} from '../types'
import ServiceModalOfferClasses, { BlankOffer } from './OfferClasses'
import { ServiceModalBooking } from './BookingSettings'
import SchedulingRules from './SchedulingRules'
import { classNames } from '@progress/kendo-react-common'
import {
    addDurationToStr,
    toHHMM,
    toYmd,
} from 'utils/string-localization-helper'
import { CategoryTypes } from 'types/category'
import GenericError from 'components/GenericError/GenericError'
import ErrorModal from '../ErrorModal'
import { useTenantContext } from 'context/TenantContext'
import { getTranslation } from 'utils/language-helper'
import { isWholeNumber } from 'utils/input-helper'

const ModalProvider = createModal(
    AuthContext,
    'ServiceModal',
    () => (
        <>
            <i className="feather feather-plus mr-4" />
            <span className="circular">Create</span>
        </>
    ),
    () => (
        <span className="inline-block w-6 h-6 text-primary text-center font-extralight">
            <i className="feather feather-chevron-left " />
        </span>
    )
)

export const ServiceModalCloser = ModalProvider.Closer

function ServiceModal() {
    const { ServiceModal } = useAuth()
    const { attributes, setAttributes } = ServiceModal
    const { translations, loadComponentTranslations } = useAppContext()
    const {
        locations,
        getServices,
        tenant,
        staff,
        getServiceCategories,
        getGroupClasses,
    } = useTenantContext()
    const [prompt_message, toggleDialog] = useState<string>('')
    const [success_message, setSuccessMessage] = useState('')
    const [error_msg, setErrorMsg] = useState('')
    const [selected_tab, setSelectedTab] = useState<number>(0)
    const [is_invalid_service, setInvalidService] = useState<boolean>(true)
    const [is_new, setIsNew] = useState<boolean>(true)
    const [tabs, setTabs] = useState<Array<any>>([])

    useEffect(() => {
        if (!translations.SERVICE_MODAL_TRANSLATION_ID) {
            loadComponentTranslations('SERVICE_MODAL_TRANSLATION_ID')
        }

        if (!translations.SERVICES_TRANSLATION_ID) {
            loadComponentTranslations('SERVICES_TRANSLATION_ID')
        }
    }, [translations])

    useEffect(() => {
        if (attributes?.activeTab) {
            setSelectedTab(attributes?.activeTab as number)
        }

        const {
            name,
            duration,
            max_participants,
            price,
            service_type,
            category,
        } = attributes || {}

        const has_missing_field = (!service_type ||
            !name ||
            !category ||
            duration <= 0 ||
            isWholeNumber(+duration) ||
            isWholeNumber(+max_participants) ||
            !max_participants ||
            max_participants < 1 ||
            price < 0) as boolean

        setInvalidService(has_missing_field)
        setIsNew(!attributes?.id)
    }, [attributes])

    useEffect(() => {
        if (!attributes || !attributes?.service_type) {
            setAttributes({
                ...attributes,
                service_type: ServiceApiTypeEnum.GROUP,
            })
        }
        return () => setSelectedTab(0)
    }, [attributes?.service_type])

    useEffect(() => {
        const is_group_or_series =
            attributes &&
            (attributes.service_type == ServiceApiTypeEnum.GROUP ||
                attributes.service_type == ServiceApiTypeEnum.SERIES)

        const hide_offer_classes_tab =
            staff?.length < 1 || locations?.length < 1

        const blank_offer_panel = (
            <BlankOffer
                onNext={handleNextClick}
                showOfferListForm={handleOfferClassClick}
            />
        )

        const offer_class_panel = (
            <>
                {' '}
                <ServiceModalOfferClasses
                    onAttributesChanged={(
                        updated_attributes: ModalDataAttributes,
                        idx: number
                    ) => {
                        const group_classes =
                            attributes?.group_classes as ModalDataAttributes[]
                        if (updated_attributes.repeats != 'WEEKLY')
                            delete updated_attributes.day_of_week
                        group_classes[idx] = updated_attributes
                        setAttributes({
                            ...attributes,
                            group_classes,
                        })
                    }}
                    removeItem={async (idx: number) => {
                        const group_classes =
                            attributes?.group_classes as ModalDataAttributes[]
                        const { id } = group_classes[idx]
                        const payload: any = {
                            ...group_classes[idx],
                            ends_at: new Date(new Date().getDate() - 1),
                        }
                        group_classes.splice(idx, 1)

                        if (id) {
                            if (payload?.is_recurring) {
                                const group_class: GroupClassApi = {
                                    id: payload?.id,
                                    effectiveDate: toYmd(
                                        getDateXDaysAgo(100)
                                    ),
                                    expiryDate: toYmd(getDateXDaysAgo(99)),
                                    startTime: payload?.time,
                                    recurring:
                                        payload?.is_recurring || false,
                                    serviceId: attributes?.id as string,
                                    duration:
                                        attributes?.duration as number,
                                    groupClassSetting: {
                                        locationId: payload?.location,
                                        staffId: payload?.staff,
                                        locationSpaceId:
                                            payload?.location_space,
                                    },
                                    recurrenceSchedule: payload?.repeats,
                                    dayOfWeek: payload?.day_of_week,
                                }

                                let del = await putApiRequest(
                                    `/v1/group_classes/${id}`,
                                    group_class
                                )
                                if (!del.ok) {
                                    toggleDialog(del?.message as string)
                                } else {
                                    setSuccessMessage(
                                        'Class removed successfully!'
                                    )
                                    setAttributes({
                                        ...attributes,
                                        group_classes,
                                    })
                                }
                            } else {
                                const hardDel = await deleteApiRequest(
                                    `/v1/group_classes/${id}?hardDelete=true`
                                )
                                if (!hardDel.ok) {
                                    toggleDialog(hardDel?.message as string)
                                } else {
                                    setSuccessMessage(
                                        'Class removed successfully!'
                                    )
                                }
                            }
                        } else {
                            setAttributes({
                                ...attributes,
                                group_classes,
                            })
                        }
                    }}
                />
            </>
        )

        const tabs: Array<any> = [
            {
                title: 'basic_info_panel_title',
                form: <GeneralForm onSubmit={onSubmit} />,
                disabled: false,
                visible: true,
            },
            {
                title: 'assign_staff_panel_title',
                form: <ServiceModalStaff />,
                disabled: is_new,
                visible: !is_group_or_series &&!is_new,
            },
            {
                title: 'memberships_panel_title',
                form: <ServiceModalMemberships />,
                disabled: is_new,
                visible: !is_new,  // Set visible to false when creating a service
            },
            {
                title: 'offer_class_panel_title',
                form:
                    (attributes?.group_classes as GroupClassFormItem[])
                        ?.length > 0
                        ? offer_class_panel
                        : blank_offer_panel,
                disabled: is_new,
                visible: hide_offer_classes_tab
            ? false
            : is_group_or_series && !is_new,  // Hide offer class during creation
            },
            {
                title: 'booking_panel_title',
                form: <ServiceModalBooking />,
                disabled: false,
                visible: true,
            },
            {
                title: 'scheduling_rules',
                form: (
                    <SchedulingRules
                        onPrevious={handlePrevClick}
                        onNext={onSubmit}
                    />
                ),
                disabled: is_new,
                // visible: true,
                visible: !is_new, // Hide offer class during creation
            },
        ]
        setTabs(tabs)
    }, [
        attributes,
        is_new,
        staff,
        locations,
        is_invalid_service,
        ServiceModal.is_open,
    ])

    function handleCloseModal() {
        setSelectedTab(0)
        setAttributes({})
        ServiceModal.close()
    }

    function handleOfferClassClick() {
        const missing_prerequisites = []
        if (locations.length > 0) {
        } else {
            missing_prerequisites.push('location')
        }
        if (staff.length > 0) {
        } else {
            missing_prerequisites.push('staff')
        }

        if (missing_prerequisites.length > 0) {
            const message = `missing_${missing_prerequisites.join('_')}`
            toggleDialog(translations[message])
        } else {
            if (attributes?.service_type == ServiceApiTypeEnum.SERIES) {
                const date = new Date()
                const series_name = getTranslation(
                    'service_modal.default_series_name',
                    translations,
                    {
                        service_name: attributes?.name as string,
                        current_month_year:
                            date.toLocaleString('default', {
                                month: 'long',
                            }) +
                            ' ' +
                            date.getFullYear(),
                    }
                )
                setAttributes({
                    ...attributes,
                    group_classes: [
                        {
                            availabilities: [
                                {
                                    date: date,
                                    start_time: toHHMM(date),
                                },
                            ],
                            location: locations && locations[0].id,
                            duration: attributes.duration as number,
                            staff: staff && staff[0].id,
                            time: toHHMM(date),
                            date: date,
                            location_space:
                                (locations &&
                                    locations?.[0]?.location_spaces?.[0]
                                        ?.id) ||
                                '',
                            name: series_name,
                            service_type: attributes.service_type,
                        },
                    ],
                })
            } else {
                setAttributes({
                    ...attributes,
                    group_classes: [
                        {
                            date: new Date(),
                            location_space:
                                (locations &&
                                    locations?.[0]?.location_spaces?.[0]
                                        ?.id) ||
                                '',
                            location: locations && locations[0].id,
                            duration: attributes?.duration as number,
                            staff: staff && staff[0].id,
                            time: toHHMM(new Date()),
                            service_type:
                                attributes?.service_type ||
                                ServiceApiTypeEnum.GROUP,
                        },
                    ],
                })
            }
        }
    }

    const getDateXDaysAgo = (numOfDays: number, date = new Date()) => {
        const daysAgo = new Date(date.getTime())

        daysAgo.setDate(date.getDate() - numOfDays)

        return daysAgo
    }

    const onSubmit = async () => {
        if (is_invalid_service) return

        let success = false
        try {
            const {
                name,
                description,
                duration,
                max_participants,
                primary_color,
                price,
                service_type,
                is_public,
                image_url,
                blocked_from, 
                blocked_to,
                member_price,
            } = attributes as unknown as ServiceItem
            const group_classes =
                (attributes?.group_classes as GroupClassFormItem[]) || []
            const { staff_assigned } = attributes as unknown as Record<
                string,
                Record<string, string>[]
            >
            const service: Record<string, string> = {}

            if (attributes && attributes.id) {
                service.id = attributes.id as string
            }

            const form_values: ServiceApiItem = {
                id: attributes?.id as string,
                category: {
                    id: attributes?.category as string,
                    type: CategoryTypes.SERVICE,
                },
                description,
                duration: duration as unknown as number,
                name,
                tenant: {
                    id: tenant?.id as string,
                },
                imageUrl: image_url as string,
                maxCapacity: max_participants as unknown as number,
                public: is_public,
                price: price ? price : 0,
                primaryColorHex: primary_color as string,
                type: service_type as unknown as ServiceApiType,
                staff: staff_assigned as unknown as {
                    id: string
                    user: UserModel
                }[],
                series: false,
                blockTimeBefore: attributes?.show_block ? blocked_from : undefined,
                blockTimeAfter: attributes?.show_block ? blocked_to : undefined,
                memberPrice: attributes?.has_member_price ? member_price : undefined,
                memberPriceType: attributes?.has_member_price ? (attributes.member_price_type as ServiceDiscountType).value : undefined,
                preventDuplicateBookingByEmail: attributes?.prevent_duplicate_booking_by_email as boolean,
                preventDuplicateBookingByPhone: attributes?.prevent_duplicate_booking_by_phone as boolean
            }

            if (form_values.type == 'SERIES') {
                form_values.series = true
            }
            let api
            if (form_values.id) {
                api = await putApiRequest(
                    `/v1/services/${form_values.id}`,
                    form_values as unknown as Record<
                        string,
                        string | number | boolean
                    >
                )
                const { list_item_idx } = attributes || {}

                setAttributes({
                    list_item_idx,
                    updated_item: api,
                    refetch: true,
                })

                if (api.id) {
                    success = true
                    setSuccessMessage('Service updated successfully')
                }
            } else {
                api = await postApiRequest(
                    '/v1/services',
                    form_values as unknown as Record<
                        string,
                        string | number | boolean
                    >
                )

                if (api.id && attributes) {
                    success = true
                    attributes.id = api.id
                    setAttributes({
                        ...attributes,
                        refetch: true,
                    })
                    setSuccessMessage('Service created successfully!')
                } else {
                    success = false
                    toggleDialog(api.message)
                }
            }
            if (success && group_classes.length > 0) {
                success = false
                let successful_classes = 0
                if (attributes?.service_type == ServiceApiTypeEnum.SERIES) {
                    group_classes.forEach(async (group_class) => {
                        const payload: GroupClassApi = {
                            id: group_class?.id as string,
                            name: group_class?.name as string,
                            serviceId: attributes.id as string,
                            groupClassAvailabilities: (
                                group_class?.availabilities as ModalDataAttributes[]
                            )?.map((availability: any) => {
                                return {
                                    id: availability?.id,
                                    date: availability.date,
                                    startTime: availability.start_time,
                                    endTime: addDurationToStr(
                                        availability.date,
                                        availability.start_time,
                                        group_class.duration as number
                                    ),
                                } as GroupClassAvailabilityApi
                            }),
                            groupClassSetting: {
                                id: group_class?.setting_id as string,
                                locationId: group_class?.location as string,
                                locationSpaceId:
                                    group_class?.location_space as string,
                                staffId: group_class?.staff as string,
                            },
                        }
                        let res
                        if (payload?.id) {
                            res = await putApiRequest(
                                '/v1/group_classes/series',
                                payload
                            )
                        } else {
                            res = await postApiRequest(
                                '/v1/group_classes/series',
                                payload
                            )
                        }
                        if (res.ok) {
                            successful_classes++
                            getGroupClasses()
                        } else {
                            setErrorMsg(res.message)
                        }
                    })
                } else {
                    group_classes.forEach(
                        async (
                            {
                                date,
                                day_of_week,
                                ends_at,
                                time,
                                is_recurring,
                                id,
                                location: locationId,
                                location_space: locationSpaceId,
                                repeats,
                                staff: staffId,
                            },
                            class_idx: number
                        ) => {
                            const group_class: GroupClassApi = {
                                id,
                                effectiveDate: toYmd(date),
                                expiryDate: attributes?.ends_at as Date,
                                startTime: time,
                                recurring: is_recurring || false,
                                duration: form_values.duration,
                                serviceId: attributes?.id as string,
                                groupClassSetting: {
                                    locationId,
                                    staffId,
                                    locationSpaceId,
                                },
                            }

                            if (group_class.recurring) {
                                group_class.dayOfWeek = day_of_week
                                group_class.recurrenceSchedule = repeats
                                if (ends_at) {
                                    group_class.expiryDate = toYmd(ends_at)
                                }
                            }
                            let offer_api
                            if (id) {
                                const { _original } =
                                    group_classes[class_idx]
                                const original =
                                    _original as unknown as Record<
                                        string,
                                        string | number | boolean | Date
                                    >
                                let has_updates = false
                                const gc = group_classes[
                                    class_idx
                                ] as unknown as Record<
                                    string,
                                    string | number | boolean | Date
                                >

                                Object.keys(original).forEach(
                                    (key: string) => {
                                        if (original[key] != gc[key])
                                            has_updates = true
                                    }
                                )

                                if (!original.ends_at && gc.ends_at) {
                                    has_updates = true
                                }

                                if (has_updates) {
                                    offer_api = await putApiRequest(
                                        `/v1/group_classes/${id}`,
                                        group_class
                                    )
                                    group_classes[class_idx].id =
                                        offer_api.id
                                    setSuccessMessage(getTranslation('common.update_grp_class', translations))    
                                }
                            } else if (
                                group_class.dayOfWeek &&
                                group_class.recurrenceSchedule &&
                                [
                                    RecurrenceSchedule.WEEKLY,
                                    RecurrenceSchedule.MONTHLY_ON_DAY,
                                ].indexOf(group_class.recurrenceSchedule) <
                                    0
                            ) {
                                toggleDialog(
                                    translations.day_of_week_only_for_weekly
                                )
                            } else {
                                offer_api = await postApiRequest(
                                    '/v1/group_classes',
                                    group_class
                                )

                                if (!offer_api.ok) {
                                    toggleDialog(offer_api.message)
                                    // setSelectedTab(3)
                                } else {
                                    const _original = {
                                        id: offer_api.id,
                                        date: new Date(
                                            offer_api.effectiveDate
                                                .toString()
                                                .concat(' ')
                                                .concat(offer_api.startTime)
                                        ),
                                        ends_at: new Date(
                                            offer_api.expiryDate
                                        ),
                                        time: offer_api.startTime,
                                        is_recurring: offer_api.recurring,
                                        recurring_schedule:
                                            offer_api.recurrenceSchedule,
                                        location:
                                            offer_api.groupClassSetting
                                                .locationId,
                                        location_space:
                                            offer_api.groupClassSetting
                                                .locationSpaceId,
                                        staff: offer_api.groupClassSetting
                                            .staffId,
                                    }

                                    group_classes[class_idx] = {
                                        ..._original,
                                        _original,
                                    }
                                    setAttributes({
                                        group_classes,
                                        refetch_classes: true,
                                    })
                                    successful_classes++
                                }
                                if (successful_classes > 0) {
                                    setSuccessMessage(getTranslation('common.success_grp_class', translations))
                                } else {
                                    setSuccessMessage('')
                                }
                            }
                        }
                    )
                }
                if (
                    successful_classes == group_classes.length &&
                    !prompt_message
                ) {
                    setSuccessMessage('Class added successfully!')
                    setAttributes({
                        group_classes,
                        refetch_classes: true,
                    })
                    ServiceModal.close()
                } else {
                    setSuccessMessage('')
                }
            } else if (success) {
                ServiceModal.close()

                setAttributes({
                    categories: attributes?.categories || [],
                    api,
                })
            }

            getServices()
            getGroupClasses()
            getServiceCategories()
            ServiceModal.close()
        } catch (e) {
            const { message } = e as Record<string, string>
            toggleDialog(message)
        }
    }

    const handleSelect = (e: TabStripSelectEventArguments) => {
        setSelectedTab(e.selected)
    }

    function handlePrevClick() {
        let prev_tab = selected_tab
        const visible_tabs = tabs.filter((tab) => tab.visible)
        for (let i = selected_tab - 1; i >= 0; i--) {
            if (visible_tabs[i] && !visible_tabs[i].disabled) {
                prev_tab = i
                break
            }
        }
        setSelectedTab(prev_tab)
    }

    function handleNextClick() {
        let next_tab = selected_tab
        const visible_tabs = tabs.filter((tab) => tab.visible)
        for (let i = selected_tab + 1; i < visible_tabs.length; i++) {
            if (visible_tabs[i] && !visible_tabs[i].disabled) {
                next_tab = i
                break
            }
        }
        setSelectedTab(next_tab)
    }

    const servicesFooter = () => {
        const isStart = selected_tab === 0
        const isEnd = selected_tab === 4
        return (
            <div className="flex justify-between">
                <div className="flex justify-start p-5 ">
                    <button
                        type="button"
                        className="border border-gray-300 rounded-lg inline-block mr-3 px-2 py-1"
                        onClick={handleCloseModal}
                    >
                        <Translation content_key="cancel" />
                    </button>
                    {selected_tab == 2 &&
                    attributes?.service_type ==
                        ServiceApiTypeEnum.SERIES ? (
                        <></>
                    ) : (
                        <button
                            disabled={is_invalid_service}
                            type="button"
                            onClick={() => {
                                onSubmit()
                            }}
                            className={classNames(
                                'group relative flex justify-center',
                                'py-1 px-2 border border-transparent',
                                'rounded-md text-white',
                                'focus:outline-none',
                                ' focus:ring-2 focus:ring-offset-2 focus:ring-primary-light',
                                is_invalid_service
                                    ? 'bg-gray-200 cursor-not-allowed'
                                    : 'bg-primary-dark'
                            )}
                        >
                            <Translation content_key="save" />
                        </button>
                    )}
                </div>
                <div className="flex justify-end p-5">
                    <button
                        disabled={selected_tab === 0}
                        type="button"
                        className={classNames(
                            'border border-gray-300 rounded-lg inline-block mr-3 px-2 py-1',
                            isStart && 'hidden'
                        )}
                        onClick={() => {
                            if (!isStart) {
                                handlePrevClick()
                            }
                        }}
                    >
                        Previous
                    </button>
                    <button
                        disabled={is_invalid_service}
                        type="button"
                        onClick={() => {
                            if (!isEnd) {
                                handleNextClick()
                            }
                        }}
                        className={classNames(
                            'border border-gray-300 rounded-lg inline-block mr-3 px-2 py-1',
                            isEnd && 'hidden',
                            is_invalid_service
                                ? 'bg-gray-300 cursor-not-allowed'
                                : ''
                        )}
                    >
                        <Translation content_key="next" />
                    </button>
                    {isEnd && (
                        <button
                            className={classNames(
                                'group relative flex justify-center',
                                'py-1 px-2 border border-transparent',
                                'rounded-md text-white',
                                'focus:outline-none',
                                'bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                            )}
                            onClick={() => onSubmit()}
                        >
                            FINISH
                        </button>
                    )}
                </div>
            </div>
        )
    }

    return (
        <>
            <ModalProvider.Visible>
                <ModalWrapper
                    zIndex="z-[29]"
                    className={classNames(
                        selected_tab == 2 &&
                            attributes &&
                            attributes.service_type ==
                                ServiceApiTypeEnum.SERIES
                            ? 'w-[80%]'
                            : ''
                    )}
                >
                    <div className="bg-white shadow-3xl w-full overflow-x-hidden sm:rounded-md mx-auto my-4 relative border-2 border-gray-100 flex flex-col">
                        <div className="flex px-4 text-gray-500 z-10 w-full pt-4 bg-opacity-30 pb-3 bg-primary-lighter">
                            <div>
                                <ul className="ml-6 text-xs flex gap-1 breadcrumbs text-gray-400">
                                    <li>Dashboard</li>
                                    <li>Service</li>
                                </ul>
                                <span
                                    className="inline-block self-center text-lg text-primary-dark"
                                    onClick={handleCloseModal}
                                >
                                    <ServiceModalCloser className="self-center" />
                                    <span className="circular">
                                        {attributes?.id ? 'Edit' : 'New'}{' '}
                                        Service
                                    </span>
                                </span>
                            </div>
                        </div>
                        <TabStrip
                            selected={selected_tab}
                            onSelect={handleSelect}
                            className="z-20 flex-1"
                            animation={false}
                        >
                            {tabs
                                .filter((tab) => tab.visible)
                                .map((tab, index) => {
                                    return (
                                        <TabStripTab
                                            key={index}
                                            title={
                                                <Translation
                                                    content_key={tab.title}
                                                />
                                            }
                                            disabled={tab.disabled}
                                            contentClassName="p-5"
                                        >
                                            {tab.form}
                                        </TabStripTab>
                                    )
                                })}
                        </TabStrip>
                        {selected_tab < 4 && servicesFooter()}
                    </div>
                </ModalWrapper>
            </ModalProvider.Visible>

            {prompt_message?.length > 0 && (
                <GenericError
                    error_message={prompt_message}
                    onCloseModal={() => {
                        toggleDialog('')
                    }}
                />
            )}

            {success_message?.length > 0 && (
                <ErrorModal
                    error_message={success_message}
                    onCloseModal={() => {
                        setSuccessMessage('')
                    }}
                    type="success"
                />
            )}
            {error_msg?.length > 0 && (
                <GenericError
                    error_message={error_msg}
                    onCloseModal={() => setErrorMsg('')}
                />
            )}
        </>
    )
}

export default ServiceModal
